import {SecondaryTimelineServerQuery} from '@github-ui/server-persisted-queries'
import type {SecondaryTimelineServerQueryQuery} from '@github-ui/server-persisted-queries/types/SecondaryTimelineServerQuery'
import {reactFetchJSON} from '@github-ui/verified-fetch'

type SecondaryTimelineNode = NonNullable<SecondaryTimelineServerQueryQuery['nodes'][number]>
export type ReferencedEventNode = Extract<SecondaryTimelineNode, {__typename: 'ReferencedEvent'}>

export type ReferencedEventCommitNode = NonNullable<ReferencedEventNode['commit']>
export type SignatureResultFromFragment = NonNullable<ReferencedEventCommitNode['signature']>
export type CertificateAttributesFromFragment = NonNullable<
  Extract<SignatureResultFromFragment, {__typename: 'SmimeSignature'}>['subject']
>

export type SecondaryTimelineEndpointArgs = {
  owner: string
  repo: string
  number: number
  /** Every `ReferencedEvent` on the rendered page, so one request serves all of their badges. */
  eventIds: readonly string[]
}

export type SecondaryTimelineSource = {readonly kind: 'endpoint'} & SecondaryTimelineEndpointArgs

export function referencedEventCommitsQueryKey({owner, repo, number, eventIds}: SecondaryTimelineEndpointArgs) {
  return ['timeline-items', 'referenced-event-commits', {owner, repo, number, eventIds}] as const
}

export function referencedEventCommitsUrl({owner, repo, number, eventIds}: SecondaryTimelineEndpointArgs): string {
  const params = new URLSearchParams({queryId: SecondaryTimelineServerQuery})
  for (const id of eventIds) params.append('nodes[]', id)

  return `/${owner}/${repo}/issues/${number}/secondary_timeline?${params}`
}

export async function fetchReferencedEventCommits(
  args: SecondaryTimelineEndpointArgs,
  signal?: AbortSignal,
): Promise<readonly ReferencedEventNode[]> {
  const response = await reactFetchJSON(referencedEventCommitsUrl(args), {signal})
  if (!response.ok) throw new Error(`Referenced event commits fetch failed (${response.status})`)

  const json = (await response.json()) as {data?: SecondaryTimelineServerQueryQuery; errors?: unknown[]}
  // One request backs every badge, so a field-level error must not blank them all. An unresolvable
  // id just goes missing, as under Relay.
  if (!json.data?.nodes) throw new Error('Referenced event commits query returned errors')

  return json.data.nodes.flatMap(node => (node?.__typename === 'ReferencedEvent' ? [node] : []))
}
